// Displays — map a signal the boat already holds onto a field your instruments
// can render (ADR 0194, core ADR-153).
//
// This page moved here from the boat's own operator page. The translation
// still happens aboard, from the boat's own database, so it keeps working with
// no internet and across a reboot; what moved is only WHERE IT IS AUTHORED.
//
// The four honesty rules ADR-150 established for the boat's page apply here
// unchanged, and are harder to keep at this distance:
//
//   1. the gateway shown is the RESOLVED one — where frames are actually
//      leaving by after pin, preference and fallback — not what was picked;
//   2. a Yacht Devices gateway restamps our frames onto its own bus address,
//      so displays credit the values to the gateway. Say it where the gateway
//      is chosen, or someone goes hunting a ghost sender;
//   3. "on the bus" means the gateway accepted the frame. It does NOT mean a
//      display rendered it. Only `receiveDevices` — the bus's own PGN 126464
//      capture — may claim an instrument reads a field;
//   4. staleness carries its age. "not sent" without a duration is a shrug.
//
// And one this page adds, because the boat is far away now: a save is a claim
// about a REQUEST. The boat can refuse, so nothing here shows a change as
// applied until the boat's own mirror reports the digest back.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  n2kOutputConfigDigest,
  n2kOutputInstanceLabel,
  n2kOutputShadowedRows,
  type N2kOutputConfig,
  type N2kOutputConfigRow,
  type N2kOutputStatePayload,
  type N2kOutputStateTarget,
} from '@neomaris/protocol';

import { useBoatChrome } from '../components/app/boatChrome';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import {
  agoLabel,
  rejectionLabel,
  saveIntentFor,
  saveN2kOutput,
  useN2kOutput,
  type SaveIntent,
  type SaveOutcome,
} from '../lib/n2kOutput';

const AGE_TICK_MS = 10_000;

/**
 * How recently a frame must have gone out for "sending" to still be true.
 *
 * The slowest mapping cadence is 2.5 s and a report is built up to 30 s after
 * the last one, so this has to clear both comfortably without being so wide
 * that a boat which stopped minutes ago still reads as active.
 */
const RECENT_TX_MS = 15_000;

/** A row the boat will keep but never transmit, because the target is gone. */
function unknownTarget(state: N2kOutputStatePayload, targetId: string): boolean {
  return !state.capability.targets.some((t) => t.id === targetId);
}

function targetById(
  state: N2kOutputStatePayload,
  targetId: string
): N2kOutputStateTarget | undefined {
  return state.capability.targets.find((t) => t.id === targetId);
}

/**
 * The instance bound for a target, as the BOAT reported it.
 *
 * A target we do not recognise, or one shipped without a bound, gets 0 — no
 * instance control. Guessing a bound here would be a third copy of core's
 * wire-field table (a tank is 4 bits, most others u8), and the copy that
 * drifts is the one that lets somebody save tank 16 and watch it transmit as
 * tank 0.
 */
function instanceMaxFor(state: N2kOutputStatePayload, targetId: string): number {
  const max = targetById(state, targetId)?.instanceMax;
  return typeof max === 'number' && Number.isFinite(max) && max > 0 ? max : 0;
}

function liveLabel(state: N2kOutputStatePayload, index: number): string {
  const status = state.status.rows.find((r) => r.index === index);
  if (!status) return '—';
  if (status.unitMismatch) return 'wrong unit — not sent';
  if (status.inputValue === null) return 'never seen';
  const value = `${status.inputValue}${status.inputUnit ? ` ${status.inputUnit}` : ''}`;
  if (status.fresh) return value;
  // Rule 4: staleness carries its age.
  const age = status.inputAgeMs === null ? '' : ` (${Math.round(status.inputAgeMs / 1000)}s old)`;
  return `${value} — stale${age}, not sent`;
}

export function Displays() {
  const { boatId, boat } = useBoatChrome();
  const { state, refresh } = useN2kOutput(boatId);
  useDocumentTitle(boat ? `${boat.display_name} — Displays` : 'Displays — Neomaris');

  const [draft, setDraft] = useState<N2kOutputConfig | null>(null);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const [saving, setSaving] = useState(false);
  // The in-flight save's idempotency key, held ACROSS retries. See
  // `saveIntentFor`: a retry after a lost response must reuse it, or the boat
  // is asked to apply an already-applied document and answers `stale_base`.
  const intentRef = useRef<SaveIntent | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const boatConfig = state.kind === 'ready' ? state.data.state.config : null;
  const boatDigest = state.kind === 'ready' ? state.data.configDigest : null;

  // The draft is discarded whenever the BOAT's configuration changes under it.
  // That is deliberate and it is the honest behaviour: the boat is the source
  // of truth, and silently keeping an edit on top of a configuration that no
  // longer exists is how someone saves a document built from a stale base and
  // gets a conflict they cannot explain.
  useEffect(() => {
    setDraft(null);
  }, [boatDigest]);

  const config = draft ?? boatConfig;
  const dirty = useMemo(
    () => config !== null && boatDigest !== null && n2kOutputConfigDigest(config) !== boatDigest,
    [config, boatDigest]
  );

  const edit = useCallback((next: N2kOutputConfig) => {
    setDraft(next);
    setOutcome(null);
  }, []);

  const setRow = useCallback(
    (index: number, patch: Partial<N2kOutputConfigRow>) => {
      if (!config) return;
      edit({
        ...config,
        rows: config.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      });
    },
    [config, edit]
  );

  /**
   * Change a row's target, clamping its instance to the new target's bound.
   *
   * Switching from an engine (u8) to a tank (4-bit) with instance 40 still set
   * would send a document the boat refuses whole — and the owner would see
   * `invalid_row` for a field the form never showed them. Clamping is the
   * honest repair: the new target's range is a different range.
   */
  const setRowTarget = useCallback(
    (index: number, targetId: string) => {
      if (!config || state.kind !== 'ready') return;
      const row = config.rows[index];
      if (!row) return;
      const max = instanceMaxFor(state.data.state, targetId);
      setRow(index, { targetId, instance: Math.min(row.instance, max) });
    },
    [config, state, setRow]
  );

  const removeRow = useCallback(
    (index: number) => {
      if (!config) return;
      edit({ ...config, rows: config.rows.filter((_, i) => i !== index) });
    },
    [config, edit]
  );

  const addRow = useCallback(() => {
    if (!config || state.kind !== 'ready') return;
    const target = state.data.state.capability.targets[0];
    if (!target) return;
    // An empty path is a legitimate starting point. Requiring a listed one made
    // the free-text field unreachable on exactly the boat that needs it: one
    // with nothing live on the bus right now cannot seed a row, so it could
    // never type an unlisted signal into one. The row is simply not saveable
    // until a path is entered, which the boat enforces anyway.
    const path = state.data.state.capability.inputPaths[0] ?? '';
    edit({
      ...config,
      // Appended, never inserted: the first row wins on a field collision, so
      // inserting would silently change which mapping transmits.
      rows: [...config.rows, { targetId: target.id, inputPath: path, instance: 0, enabled: true }],
    });
  }, [config, state, edit]);

  const save = useCallback(async () => {
    if (!config || boatDigest === null) return;
    setSaving(true);
    const intent = saveIntentFor(config, boatDigest, intentRef.current);
    intentRef.current = intent;
    const result = await saveN2kOutput(boatId, config, boatDigest, intent);
    setOutcome(result);
    setSaving(false);
    if (result.kind === 'sent') void refresh();
  }, [config, boatDigest, boatId, refresh]);

  if (state.kind === 'loading') {
    return (
      <section data-testid="displays">
        <div className="page-head">
          <h1>Displays</h1>
        </div>
        <p className="muted">Reading what your boat is sending…</p>
      </section>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <section data-testid="displays">
        <div className="page-head">
          <h1>Displays</h1>
        </div>
        <div className="panel" data-testid="displays-unavailable">
          <h3>Waiting for your boat</h3>
          <p className="muted">
            Your boat hasn’t told us what it can send yet. Which signals and gateways are available
            depends on the hardware aboard, so this page fills in shortly after the boat connects.
            Nothing is wrong with the boat’s output in the meantime — it keeps sending whatever it
            was already sending.
          </p>
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section data-testid="displays">
        <div className="page-head">
          <h1>Displays</h1>
        </div>
        <div className="panel">
          <h3>Couldn’t load this</h3>
          <p className="muted">{state.message}</p>
        </div>
      </section>
    );
  }

  const payload = state.data.state;
  const localAuthored = state.data.authority === 'local';
  /*
    "Sending" is a claim about NOW, so it needs evidence about now.
    `framesSent` is a lifetime total and never goes down: once a boat had ever
    transmitted, it would keep claiming to be sending after its signals went
    stale or its gateway changed. `lastTxAtMs` and `generatedAt` are both the
    BOAT's clock, so comparing them stays consistent even when that clock is
    wrong in absolute terms — which this feature already assumes it can be.
  */
  const transmittingNow = (() => {
    const last = payload.status.lastTxAtMs;
    if (last === null || !payload.status.running) return false;
    const reportedAt = Date.parse(payload.generatedAt);
    if (!Number.isFinite(reportedAt)) return false;
    return reportedAt - last <= RECENT_TX_MS;
  })();
  const shadowed = new Set(config ? n2kOutputShadowedRows(config) : []);
  const resolved = payload.capability.gateways.find(
    (g) => g.id === payload.status.resolvedEgressId
  );

  return (
    <section data-testid="displays">
      <div className="page-head">
        <h1>Displays</h1>
        <p className="page-sub">
          Send a signal your boat already has out to your instrument displays — an engine
          temperature from a sensor the display can’t read, a depth from another bus. Your boat
          keeps this setup and keeps sending it with no internet.
        </p>
      </div>

      {/* The boat refused something. Shown before anything editable, because
          it explains why the table below is not what someone just saved. */}
      {payload.lastRejection ? (
        <div className="panel" data-testid="displays-rejection">
          <h3>Your boat didn’t accept the last change</h3>
          <p className="muted">{rejectionLabel(payload.lastRejection.reason)}</p>
          {payload.lastRejection.detail ? (
            <p className="muted">{payload.lastRejection.detail}</p>
          ) : null}
        </div>
      ) : null}

      {localAuthored ? (
        <div className="panel" data-testid="displays-local-authored">
          <h3>This boat sets this up itself</h3>
          <p className="muted">
            It hasn’t been linked to an account yet, so it’s configured from its own page aboard.
            This view is read-only.
          </p>
        </div>
      ) : null}

      <div className="panel" data-testid="displays-output">
        <h3>Output</h3>
        <label className="dp-toggle">
          <input
            type="checkbox"
            checked={config?.enabled ?? false}
            disabled={localAuthored || !config}
            onChange={(e) => config && edit({ ...config, enabled: e.target.checked })}
          />
          Send these signals to your displays
        </label>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label htmlFor="displays-gateway">Gateway</label>
          <select
            id="displays-gateway"
            className="form-input"
            value={config?.egressSourceId ?? ''}
            disabled={localAuthored || !config}
            onChange={(e) => config && edit({ ...config, egressSourceId: e.target.value || null })}
          >
            <option value="">Use the boat’s main gateway</option>
            {payload.capability.gateways.map((gateway) => (
              <option key={gateway.id} value={gateway.id} disabled={!gateway.writable}>
                {gateway.label}
                {gateway.writable ? '' : ' — can’t send'}
                {gateway.preferred ? ' (main)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/*
          Rule 1, and it is a claim about EVIDENCE rather than about
          configuration. A resolved gateway only says the boat worked out where
          frames would go; frames actually having gone is a separate fact, and
          saying "sending through" on the strength of the first would be the
          page asserting something it has not been told. So the three states are
          distinguished: off, ready-but-nothing-sent, and genuinely sending.
        */}
        <p className="muted" data-testid="displays-transmit">
          {!payload.status.resolvedEgressId
            ? 'Nothing is going out yet. Once a mapping below has a live signal, your boat picks a gateway and starts sending.'
            : transmittingNow
              ? `Sending through ${resolved?.label ?? payload.status.resolvedEgressId}. ${payload.status.framesSent} frames so far.`
              : `Ready to send through ${resolved?.label ?? payload.status.resolvedEgressId}. Nothing is going out right now — a mapping needs a live signal first.`}
        </p>

        {/* Rule 2. */}
        {resolved?.restampsSource ? (
          <p className="muted" data-testid="displays-restamp">
            {resolved.label} sends under its own address, so your displays will credit these values
            to the gateway rather than to Neomaris.
          </p>
        ) : null}
      </div>

      <div className="panel" data-testid="displays-mappings">
        <h3>Mappings</h3>
        {config && config.rows.length === 0 ? (
          <p className="muted">
            Nothing is mapped yet. Add one to send a signal out to your displays.
          </p>
        ) : null}

        <div className="dp-rows">
          {config?.rows.map((row, index) => {
            const missing = unknownTarget(payload, row.targetId);
            const live = payload.status.rows.find((r) => r.index === index);
            const notSending = Boolean(live && (!live.fresh || live.unitMismatch));
            return (
              <div key={index} className="dp-row" data-testid="displays-row">
                <div className="form-group">
                  <label htmlFor={`displays-signal-${index}`}>Signal</label>
                  {/*
                    A LIST PLUS FREE TEXT, which is what the contract specifies
                    and what the boat's own page has always offered. The boat
                    caps the paths it ships at 400, so a busy bus genuinely has
                    signals that are not in this list — with a bare <select>
                    those would be unmappable, and the cap would have quietly
                    become a limit on the feature rather than on the frame.
                    A datalist keeps the common case one click while leaving the
                    rest reachable; the boat validates whatever is typed.
                  */}
                  <input
                    id={`displays-signal-${index}`}
                    className="form-input"
                    list={`displays-paths-${index}`}
                    value={row.inputPath}
                    disabled={localAuthored}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder="environment.outside.temperature"
                    onChange={(e) => setRow(index, { inputPath: e.target.value.trim() })}
                  />
                  <datalist id={`displays-paths-${index}`}>
                    {payload.capability.inputPaths.map((path) => (
                      <option key={path} value={path} />
                    ))}
                  </datalist>
                  {payload.capability.inputPaths.includes(row.inputPath) ? null : (
                    <p className="dp-row__meta">Not among the signals your boat is sending now.</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor={`displays-target-${index}`}>Sent as</label>
                  <select
                    id={`displays-target-${index}`}
                    className="form-input"
                    value={row.targetId}
                    disabled={localAuthored}
                    onChange={(e) => setRowTarget(index, e.target.value)}
                  >
                    {missing ? (
                      <option value={row.targetId}>
                        {row.targetId} — your boat’s software doesn’t have this
                      </option>
                    ) : null}
                    {payload.capability.targets.map((target) => (
                      <option key={target.id} value={target.id}>
                        {target.label}
                      </option>
                    ))}
                  </select>
                  {/* Rule 3: only the bus's own receive capture may claim a
                    display reads this field. */}
                  {(() => {
                    const target = payload.capability.targets.find((t) => t.id === row.targetId);
                    if (!target) return null;
                    // Old mirrors can outlive the cloud build that accepted
                    // them. Keep the render safe even if one predates the
                    // boundary sanitizer that guarantees this array.
                    const receiveDevices = Array.isArray(target.receiveDevices)
                      ? target.receiveDevices
                      : [];
                    return (
                      <p className="dp-row__meta">
                        PGN {target.pgn}
                        {receiveDevices.length ? ` — read by ${receiveDevices.join(', ')}` : ''}
                      </p>
                    );
                  })()}
                </div>

                {/*
                  WHICH engine, tank or battery. Rendered only for targets that
                  actually have an instance slot, and bounded by the max the
                  BOAT reported — a two-engine boat cannot map its starboard
                  engine at all without this, and a hidden instance left over
                  from a previous target is a document the boat refuses whole.
                */}
                {instanceMaxFor(payload, row.targetId) > 0 ? (
                  <div className="form-group">
                    <label htmlFor={`displays-instance-${index}`}>
                      {n2kOutputInstanceLabel(
                        targetById(payload, row.targetId)?.instanceKind ?? ''
                      ) || 'Instance'}
                    </label>
                    <input
                      id={`displays-instance-${index}`}
                      className="form-input"
                      type="number"
                      min={0}
                      max={instanceMaxFor(payload, row.targetId)}
                      step={1}
                      value={row.instance}
                      disabled={localAuthored}
                      data-testid="displays-instance"
                      onChange={(e) => {
                        const max = instanceMaxFor(payload, row.targetId);
                        const parsed = Number.parseInt(e.target.value, 10);
                        // Clamp rather than let an out-of-range value through:
                        // the boat refuses the WHOLE document over one bad row,
                        // and `min`/`max` on a number input are advisory —
                        // typing past them is not blocked.
                        const next = Number.isFinite(parsed)
                          ? Math.min(Math.max(parsed, 0), max)
                          : 0;
                        setRow(index, { instance: next });
                      }}
                    />
                    <p className="dp-row__meta">0 to {instanceMaxFor(payload, row.targetId)}</p>
                  </div>
                ) : null}

                <div>
                  <label className="dp-toggle">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled={localAuthored}
                      onChange={(e) => setRow(index, { enabled: e.target.checked })}
                    />
                    Send this one
                  </label>
                  <p
                    className={`dp-row__meta dp-live${notSending ? ' dp-live--warn' : ''}`}
                    data-testid="displays-live"
                  >
                    {liveLabel(payload, index)}
                  </p>
                  {shadowed.has(index) ? (
                    <p className="dp-row__meta dp-note--warn" data-testid="displays-shadowed">
                      Another mapping above already fills this field, so your boat ignores this one.
                    </p>
                  ) : null}
                </div>

                <div className="dp-row__actions">
                  {localAuthored ? null : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeRow(index)}
                      aria-label={`Remove the mapping for ${row.inputPath}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {localAuthored ? null : (
          <button
            type="button"
            className="btn"
            style={{ marginTop: 'var(--sp-3, 0.75rem)' }}
            onClick={addRow}
            disabled={payload.capability.targets.length === 0}
            data-testid="displays-add"
          >
            Add a mapping
          </button>
        )}
      </div>

      {localAuthored ? null : (
        <div className="panel" data-testid="displays-save">
          <div className="dp-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void save()}
              disabled={!dirty || saving}
            >
              {saving ? 'Sending to your boat…' : 'Save to boat'}
            </button>
            {dirty ? <span className="muted">Not sent to your boat yet.</span> : null}
          </div>

          {/* A save is a claim about a REQUEST. The confirmation someone
              actually wants — "the boat is doing this now" — is the mirror
              catching up, which the line below reports rather than asserting. */}
          {outcome?.kind === 'sent' ? (
            <p className="muted" data-testid="displays-sent">
              {boatDigest === outcome.configDigest
                ? 'Your boat has this now.'
                : 'Sent to your boat. This page will confirm once the boat reports back.'}
            </p>
          ) : null}
          {outcome?.kind === 'refused' ? (
            <p className="muted" data-testid="displays-refused">
              {outcome.message}
            </p>
          ) : null}
          {outcome?.kind === 'error' ? <p className="muted">{outcome.message}</p> : null}
        </div>
      )}

      <p className="dp-foot" data-testid="displays-freshness">
        {/*
          The CLOUD's clock, not the boat's. `generatedAt` is the boat's, and
          this feature already refuses to trust that clock for ordering (it
          uses the authority epoch instead) — so trusting it for the freshness
          claim would be the same mistake in a place the owner can see: a boat
          with a flat RTC, transmitting perfectly, would read "reported 25
          years ago".
        */}
        Showing what your boat reported {agoLabel(state.data.receivedAt, now)}.
        {payload.capability.inputPathsTruncated
          ? ' Your boat has more signals than the list can hold — you can type any signal name in full.'
          : ''}
      </p>
    </section>
  );
}
